import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import GuestAddOnTooltip from '@/features/booking/components/GuestAddOnTooltip';
import type { AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';
import type { FloorPlanResponse, FloorPlanItemResponse } from '@/features/booking/types/floorPlanTypes';

import MobileTablePreviewCard from '@/features/booking/components/mobile/MobileTablePreviewCard';
import MobileAddOnSelector from '@/features/booking/components/mobile/MobileAddOnSelector';
import { ShoppingCart } from 'lucide-react';

interface DesktopTableSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Preview Card Props
  floorPlan: FloorPlanResponse | null;
  selectedTable: string | null;
  floorPlanItems: FloorPlanItemResponse[];
  pricePerGuest: number;
  currency?: string;
  categoryImageId?: string | null;
  selectedTimeSlot?: string | null;
  businessTimeZone?: string;
  tableSize?: number | null;
  itemType?: string;
  onAddClick: () => void;
  guestCount: number;
  
  // Addon Props
  addOns: AddOnScheduleData[];
  selectedAddOns: Record<string, number>;
  onAddOnToggle: (addOnId: string, isSelected: boolean, guestIndex?: number) => void;
  addOnIdsPerGuest: string[][];
  onQuantityChange: (addOnId: string, guestIndex: number, quantity: number) => void;
  
  // Extra info for addons early entry
  eventStartTime?: string | null;
  earlyEntryDuration?: number | null;
  
  // Cart Actions
  onCartAction?: () => void;
  isSyncingCart?: boolean;
  cartButtonText?: string;
  hasCartChanges?: boolean;
  hasCartItemForEBC?: boolean;
}

const DesktopTableSelectionModal: React.FC<DesktopTableSelectionModalProps> = ({
  isOpen,
  onClose,
  floorPlan,
  selectedTable,
  floorPlanItems,
  pricePerGuest,
  currency = 'USD',
  categoryImageId,
  selectedTimeSlot,
  businessTimeZone,
  tableSize,
  itemType,
  onAddClick,
  guestCount,
  addOns,
  selectedAddOns,
  onAddOnToggle,
  addOnIdsPerGuest,
  onQuantityChange,
  eventStartTime,
  earlyEntryDuration,
  onCartAction,
  isSyncingCart,
  cartButtonText,
  hasCartChanges,
  hasCartItemForEBC,
}) => {
  const [view, setView] = useState<'main' | 'addonDetail'>('main');
  const [activeAddOn, setActiveAddOn] = useState<AddOnScheduleData | null>(null);

  // Reset view when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setView('main');
        setActiveAddOn(null);
      }, 300); // Wait for transition
    }
  }, [isOpen]);

  const handleAddOnClick = (addOnSchedule: AddOnScheduleData) => {
    setActiveAddOn(addOnSchedule);
    setView('addonDetail');
  };

  const handleBackToMain = () => {
    setView('main');
    setActiveAddOn(null);
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen || !selectedTable) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        style={{ backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
        onClick={onClose}
      />

      {/* Light glow effect behind the modal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-[65]"
      >
        <div className="w-[60vw] h-[60vh] max-w-[600px] max-h-[800px] bg-white/10 rounded-full blur-[120px]" />
      </motion.div>
      
      {/* Modal Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="relative z-[70] bg-[#111] rounded-[2rem] overflow-hidden shadow-[0_0_80px_rgba(255,255,255,0.15)] border border-white/10 flex flex-col mx-auto"
        style={{ 
          height: 'min(90vh, 1200px)', 
          aspectRatio: '650/800',
        }}
      >
        <div className="flex-1 overflow-x-hidden overflow-y-auto scrollbar-hide relative w-full h-full">
          <AnimatePresence mode='popLayout' initial={false}>
              {view === 'main' ? (
                <motion.div
                  key="main-view"
                  initial={{ opacity: 0, x: -50, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -50, filter: 'blur(4px)' }}
                  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                  className="flex flex-col"
                >
                  <div className="flex flex-col h-full w-full">
                    {/* Close button for main view */}
                    <div className="sticky top-4 right-4 z-[90] self-end mr-4 -mb-12">
                      <button 
                        onClick={onClose}
                        className="p-2 bg-black/50 hover:bg-black/80 rounded-full text-white backdrop-blur-md transition-colors shadow-lg"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="relative w-full z-10">
                      <MobileTablePreviewCard
                      floorPlan={floorPlan}
                      selectedTable={selectedTable}
                      floorPlanItems={floorPlanItems}
                      pricePerGuest={pricePerGuest}
                      currency={currency}
                      categoryImageId={categoryImageId}
                      selectedTimeSlot={selectedTimeSlot}
                      businessTimeZone={businessTimeZone}
                      tableSize={tableSize}
                      itemType={itemType}
                      onAddClick={onAddClick}
                      guestCount={guestCount}
                      eventStartTime={eventStartTime}
                    />
                  </div>
                  
                  <div className="mt-4 px-6 pb-8">
                    <MobileAddOnSelector
                      addOns={addOns}
                      selectedAddOns={selectedAddOns}
                      onAddOnToggle={onAddOnToggle}
                      onAddOnClick={handleAddOnClick}
                      guestCount={guestCount}
                      addOnIdsPerGuest={addOnIdsPerGuest}
                      currency={currency}
                    />
                  </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="addon-detail-view"
                  initial={{ opacity: 0, x: 50, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: 50, filter: 'blur(4px)' }}
                  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                  className="w-full h-full bg-[#111] flex flex-col"
                >
                  {activeAddOn && (
                     <AddOnDetailInternal 
                       addOn={activeAddOn}
                       isSelected={selectedAddOns[activeAddOn.add_on.id] > 0}
                       onToggle={onAddOnToggle}
                       onQuantityChange={onQuantityChange}
                       guestCount={guestCount}
                       remainingInventory={activeAddOn.remaining_inventory}
                       addOnIdsPerGuest={addOnIdsPerGuest}
                       allAddOns={addOns}
                       eventStartTime={eventStartTime}
                       earlyEntryDuration={earlyEntryDuration}
                       timezone={businessTimeZone}
                       currency={currency}
                       onBack={handleBackToMain}
                     />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Sticky Cart Action Button */}
          <AnimatePresence>
            {(guestCount > 0 || hasCartItemForEBC) && onCartAction && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="absolute bottom-6 right-6 z-[100]"
              >
                {hasCartItemForEBC && !hasCartChanges ? (
                  <button
                    disabled
                    className="bg-blue-900 text-blue-300 border-blue-500/30 px-6 py-3 min-w-[140px] rounded-xl flex items-center justify-center space-x-2 border font-bold text-[15px] disabled:opacity-70 disabled:cursor-not-allowed shadow-2xl transition-all duration-200"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>In Cart</span>
                  </button>
                ) : (
                  <button
                    onClick={onCartAction}
                    disabled={isSyncingCart}
                    className={`${
                      hasCartItemForEBC 
                        ? 'bg-yellow-500 hover:bg-yellow-400 text-black border-yellow-400 shadow-[0_0_20px_rgba(234,179,8,0.3)]' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500/50 shadow-2xl'
                    } px-6 py-3 min-w-[140px] rounded-xl flex items-center justify-center space-x-2 transition-all duration-200 border font-bold text-[15px] disabled:opacity-70 disabled:cursor-not-allowed`}
                  >
                    {isSyncingCart ? (
                      <>
                        <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${hasCartItemForEBC ? 'border-black/30' : 'border-white/30'}`} />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        <span>{hasCartItemForEBC ? 'Update Cart' : (cartButtonText || 'Add to Cart')}</span>
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
      </motion.div>
    </div>
  );
};

// Internal component for Addon Details matching mobile flow
const AddOnDetailInternal: React.FC<{
  addOn: AddOnScheduleData;
  isSelected: boolean;
  onToggle: (addOnId: string, isSelected: boolean, guestIndex?: number) => void;
  onQuantityChange: (addOnId: string, guestIndex: number, quantity: number) => void;
  guestCount: number;
  remainingInventory: number | null;
  addOnIdsPerGuest: string[][];
  allAddOns: AddOnScheduleData[];
  eventStartTime?: string | null;
  earlyEntryDuration?: number | null;
  timezone?: string;
  currency: string;
  onBack: () => void;
}> = ({
  addOn, isSelected, onToggle, onQuantityChange, guestCount, remainingInventory, addOnIdsPerGuest, allAddOns, eventStartTime, earlyEntryDuration, timezone, currency, onBack
}) => {
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);
  const hasValidImage = addOn.add_on.image_id && addOn.add_on.image_id !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.add_on.image_id, 'image').url : null;
  
  const isUnlimited = remainingInventory === null;
  const canApplyToAllGuests = isUnlimited || (remainingInventory as number) >= guestCount;
  const isInventoryLimited = !isUnlimited && (remainingInventory as number) < guestCount && (remainingInventory as number) > 0;
  const addOnId = addOn.add_on.id;
  const allowMultiple = addOn.add_on.allow_multiple_per_guest;

  const handleToggle = () => {
    if (guestCount > 0) {
      onToggle(addOnId, !isSelected);
    }
  };

  const getAddOnCount = () => {
    return addOnIdsPerGuest.reduce((count, guestAddOns) => 
      count + guestAddOns.filter(id => id === addOnId).length, 0
    );
  };

  const getGuestQuantity = (guestIndex: number): number => {
    const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
    return guestAddOns.filter(id => id === addOnId).length;
  };

  const getRemainingForGuest = (guestIndex: number): number | null => {
    if (isUnlimited) return null;
    let usedByOthers = 0;
    addOnIdsPerGuest.forEach((guestAddOns, idx) => {
      if (idx !== guestIndex) {
        usedByOthers += guestAddOns.filter(id => id === addOnId).length;
      }
    });
    return (remainingInventory as number) - usedByOthers;
  };

  const count = getAddOnCount();

  return (
    <div className="w-full flex-1 flex flex-col pb-8">
       {/* Details Header */}
       <div 
         className="sticky top-0 z-20 flex flex-col bg-[#111] border-b border-gray-800 shrink-0 shadow-sm"
       >
          {/* Back button + title */}
          <div className="flex items-center px-6 pb-4 pt-4">
            <button 
              onClick={onBack}
              className="mr-3 p-2 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-white flex-1 truncate">{addOn.add_on.name}</h3>
          </div>
       </div>

       <div className="p-6 space-y-6">
          {/* Image */}
          <div className="relative h-64 sm:h-72 rounded-xl overflow-hidden shadow-lg border border-white/5">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: imageUrl 
                  ? `url(${imageUrl})` 
                  : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent" />
          </div>

          {/* Info */}
          <div className="space-y-3">
             <p className="text-gray-300 leading-relaxed text-base">{addOn.add_on.description}</p>
             <div className="flex items-center justify-between mt-2">
                <div className="text-2xl font-bold text-blue-400">
                  {formatPrice(addOn.price)}
                </div>
                {isUnlimited ? (
                  <div className="text-sm text-gray-400 font-semibold tracking-wide">Unlimited</div>
                ) : (remainingInventory as number) < 10 ? (
                  <div className="text-sm font-bold text-red-400">
                    Only {remainingInventory} left!
                  </div>
                ) : (
                  <div className="text-sm font-semibold text-gray-400">
                    {remainingInventory} available
                  </div>
                )}
             </div>
          </div>

          {/* Inventory Warning */}
          {isInventoryLimited && (
            <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="text-yellow-400 mt-0.5">⚠️</div>
                <div>
                  <h5 className="text-yellow-400 font-semibold text-sm mb-1">Limited Inventory</h5>
                  <p className="text-yellow-200/80 text-sm leading-relaxed">
                    Only {remainingInventory} available for {guestCount} guests. You will need to manually assign it below.
                  </p>
                </div>
              </div>
            </div>
          )}

           {/* Toggle */}
           <div className="flex items-center justify-between p-5 bg-gray-800/80 rounded-xl border border-white/5 shadow-inner">
              <div className="flex-1 pr-4">
                <div className="text-white font-bold mb-1 text-lg">
                  {isSelected ? 'Selected' : 'Add to Booking'}
                </div>
                <div className="text-sm text-gray-400 leading-tight">
                  {isSelected 
                    ? count > 0 
                      ? (
                          <span>
                            {count} qty × {formatPrice(addOn.price)} = <span className="text-blue-400 font-semibold">{formatPrice(addOn.price * count)}</span>
                          </span>
                        )
                      : 'Selected but not assigned'
                    : canApplyToAllGuests 
                      ? `Will apply to all ${guestCount} guests`
                      : 'Manual assignment required'
                  }
                </div>
              </div>
              <button
                onClick={handleToggle}
                disabled={guestCount === 0}
                className={`w-14 h-8 shrink-0 rounded-full transition-colors duration-200 shadow-md relative ${
                  guestCount === 0
                    ? 'bg-gray-600 cursor-not-allowed opacity-50'
                    : isSelected 
                    ? 'bg-blue-600' 
                    : 'bg-white/30 hover:bg-white/40'
                }`}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full transition-transform duration-200 shadow-sm absolute top-1 ${
                    isSelected ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

          {/* Early Entry */}
          {addOn.add_on.name === 'Early Entry' && addOn.add_on.is_platform_add_on && eventStartTime && earlyEntryDuration && (
              <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/50 rounded-lg p-4 shadow-lg mt-4">
                <h5 className="text-emerald-300 font-bold text-base mb-1.5">You can take entry early!</h5>
                <p className="text-emerald-100/90 text-sm">
                  Earliest Entry Time:{' '}
                  <span className="font-bold text-white">
                    {new Date(new Date(eventStartTime).getTime() - (earlyEntryDuration / 1_000_000)).toLocaleTimeString('en-US', { 
                      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: timezone
                    })}
                  </span>
                </p>
              </div>
          )}

            {/* Guest Select */}
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="pt-2"
                >
                  <h5 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 px-1">
                    {allowMultiple ? 'Quantity per Guest' : 'Assign to Guests'}
                  </h5>
                  <div className={`grid gap-2 sm:gap-3 ${allowMultiple ? 'grid-cols-2' : 'grid-cols-4'}`}>
                    {Array.from({ length: guestCount }, (_, guestIndex) => {
                      const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
                      const hasAddOn = guestAddOns.includes(addOnId);
                      const quantity = getGuestQuantity(guestIndex);
                      const remaining = getRemainingForGuest(guestIndex);
                      const maxForGuest = remaining === null ? Infinity : remaining + quantity;

                      return (
                        <div key={guestIndex}>
                           {allowMultiple && onQuantityChange ? (
                              <div className={`flex flex-col items-center justify-center p-3 sm:p-4 gap-3 rounded-xl border transition-colors ${
                                quantity > 0 ? 'bg-blue-600/20 border-blue-500/40 text-blue-100' : 'bg-gray-800/80 border-gray-700 text-gray-300'
                              }`}>
                                <div className="flex flex-col items-center text-center h-[40px] justify-start">
                                  <span className="text-sm font-bold tracking-wide">Guest {guestIndex + 1}</span>
                                  {quantity > 0 ? (
                                    <span className="text-xs text-blue-400 mt-1 font-medium">{formatPrice(addOn.price * quantity)}</span>
                                  ) : (
                                    <span className="text-xs text-transparent mt-1 font-medium invisible">{formatPrice(addOn.price)}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4">
                                  <button
                                    onClick={() => onQuantityChange(addOnId, guestIndex, Math.max(0, quantity - 1))}
                                    disabled={quantity === 0}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                      quantity === 0 ? 'bg-gray-700/50 text-gray-500' : 'bg-gray-700 text-white hover:bg-gray-600 active:scale-95'
                                    }`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" /></svg>
                                  </button>
                                  <span className={`w-6 text-center font-bold text-base ${quantity > 0 ? 'text-white' : 'text-gray-400'}`}>
                                    {quantity}
                                  </span>
                                  <button
                                    onClick={() => onQuantityChange(addOnId, guestIndex, quantity + 1)}
                                    disabled={quantity >= maxForGuest}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                      quantity >= maxForGuest ? 'bg-gray-700/50 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-500 active:scale-95 shadow-md'
                                    }`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                                  </button>
                                </div>
                              </div>
                           ) : (
                              <GuestAddOnTooltip guestIndex={guestIndex} guestAddOns={guestAddOns} allAddOns={allAddOns}>
                                <button
                                  onClick={() => onToggle(addOnId, !hasAddOn, guestIndex)}
                                  className={`w-full py-2.5 sm:py-3 px-2 sm:px-4 rounded-xl text-xs sm:text-sm font-bold transition-all border ${
                                    hasAddOn
                                      ? 'bg-blue-600 text-white border-blue-500 shadow-lg'
                                      : 'bg-gray-800/80 text-gray-300 border-gray-700 hover:bg-gray-700'
                                  }`}
                                >
                                  Guest {guestIndex + 1}
                                </button>
                              </GuestAddOnTooltip>
                           )}
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
       </div>
    </div>
  );
};

export default DesktopTableSelectionModal;

